/* eslint-disable no-undef */
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faHomeLgAlt } from '@fortawesome/pro-regular-svg-icons/faHomeLgAlt';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { faTrophy } from '@fortawesome/pro-regular-svg-icons/faTrophy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BathsIcon from '@propertypal/shared/src/resources/icons/property/baths-icon.svg';
import BedsIcon from '@propertypal/shared/src/resources/icons/property/beds-icon.svg';
import ReceptionsIcon from '@propertypal/shared/src/resources/icons/property/receptions-icon.svg';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import getOpeningTimes from '@propertypal/shared/src/utils/agent/getOpeningTimes';
import getContact from '@propertypal/shared/src/utils/enquiries/getContact';
import getSellingAgent, { getSellingAgentColor } from '@propertypal/shared/src/utils/property/getSellingAgent';
import puralise from '@propertypal/shared/src/utils/puralise';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import AgentAwards from '../agents/AgentAwards';
import Button from '../buttons/Button';
import HorizontalDivider from '../divider/HorizontalDivider';
import useChildRemover from '../hooks/useChildRemover';
import Chevron from '../icons/Chevron';
import { RowAlignCenter } from '../layout';
import { Heading, Text, LinkText } from '../typography';
import EnergyRatingIcon from './EnergyRatingIcon';
import OpenViewingOverlay from './OpenViewingOverlay';
import PropertyPrice from './PropertyPrice';
import {
  Container,
  KeyDetails,
  SummaryItem,
  Title,
  AgentBox,
  AgentLogo,
  AgentText,
  CallAgentBox,
  ShowPhoneNumber,
  AgentBoxFixed,
  AgentBoxFixedButtons,
  LicenceWarning,
  NameAndPSRTextWrapper,
  AwardLogo,
  SummaryBox,
  AgentBoxHeader,
  AgentBoxContent,
  AgentExtraInfo,
  AgentListItem,
  IconBox,
  ShowMoreDetails,
  AgentButtons,
} from './PropertySummary.style';
import ShowHomesBox from './ShowHomesBox';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string) => void;
  onEnquiryClick: () => void;
  onRegisterInterestClick: () => void;
  onRequestValuationClick: () => void;
  registerInterest?: boolean;
  requestValuation?: boolean;
}

const PhoneNumberButton = (
  props: Pick<Props, 'containerStyle' | 'onCallClick' | 'onShowNumberClick'> & { color?: string; location: string },
) => {
  return (
    <>
      <ShowPhoneNumber data-testid="showPhoneNumber">
        <Button
          containerStyle={props.containerStyle}
          color={props.color}
          onClick={() => props.onShowNumberClick(props.location)}
          testID="showPhoneNumberButton"
          type="button"
        >
          Call the Agent
        </Button>
      </ShowPhoneNumber>

      <CallAgentBox>
        <Button
          containerStyle={props.containerStyle}
          onClick={() => props.onCallClick(props.location)}
          testID="callAgentButton"
          type="button"
          color={props.color}
        >
          Call the Agent
        </Button>
      </CallAgentBox>
    </>
  );
};

const PropertySummary: FunctionComponent<Props> = (props) => {
  const { property } = props;
  const theme = useTheme();
  const sellingAgent = getSellingAgent(property);
  const [agentColor, buttonColor] = getSellingAgentColor(property);
  const [showMore, setShowMore] = useState(false);
  const agentAward = sellingAgent?.propertypalAwards?.length ? sellingAgent?.propertypalAwards[0] : null;
  const timeLabels = getOpeningTimes(sellingAgent?.openingTimes || []);
  const contactBox = useRef<HTMLDivElement | null>(null);
  const keyDetails = useRef<HTMLDivElement>(null);
  const [showEnquiryBar, setShowEnquiryBar] = useState(false);
  const contactInfo = getContact(property);
  const page = property.type === 'development' ? 'DDP' : 'PDP';
  const showKeyDetails =
    property.numBedrooms || property.numBathrooms || property.numReceptionRooms || property.epc || property.ber;

  // only add comma add end of address if address is more than 1 word
  // particularly will find this for ROI properties
  const addressAppend = () => {
    // handles potentially hyphenated addresses
    const words = props.property.displayAddressLine1.replace('-', '').match(/(\w+)/g);
    if (words && words.length > 1) return ',';
    return null;
  };

  const enquireNowClick = (location: string) => {
    trackGaEvent('enquiry_click', { page, location });
    props.onEnquiryClick();
  };

  const registerInterestClick = (location: string) => {
    trackGaEvent('register_interest_click', { page, location });
    props.onRegisterInterestClick();
  };

  const requestValuationClick = () => {
    trackGaEvent('request_valuation_click', { page, location: 'Enquiry Box' });
    props.onRequestValuationClick();
  };

  useChildRemover(keyDetails.current);

  useEffect(() => {
    const checkBelowThreshold: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        setShowEnquiryBar(entry.boundingClientRect.top < 0);
      });
    };

    const observer = new IntersectionObserver(checkBelowThreshold, {
      threshold: 1,
    });

    if (contactBox.current) {
      observer.observe(contactBox.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Container className="pp-property-summary">
        <SummaryBox>
          {props.property.openViewing && (
            <OpenViewingOverlay
              containerStyle={{ marginRight: 10, marginBottom: 15 }}
              openViewing={props.property.openViewing}
              themeName="light"
            />
          )}

          <Heading fontWeight="600" data-testid="addressLine1">
            {props.property.displayAddressLine1}
            {addressAppend()}
          </Heading>

          <Text fontSize={24} color={theme.textLight}>
            {props.property.displayAddressLine2}
          </Text>

          <Text fontSize={18} mt={10} color={theme.textLight}>
            {props.property.briefText}
          </Text>

          {props.property.development && (
            <Text fontSize={16} mt={15} color={theme.primary} className="pp-development-link-text">
              <Link href={props.property.development.path} style={{ textDecoration: 'underline' }}>
                This property forms part of the <b>{props.property.development.name}</b> development{' '}
                <Chevron size={9} />
              </Link>
            </Text>
          )}

          <PropertyPrice
            fontSize={24}
            price={props.property.price}
            alternativeCurrencyPrice={props.property.alternativeCurrencyPrice}
            containerStyle={{ marginTop: 40 }}
            status={props.property.status}
            comingSoonText={props.property.comingSoonText}
            statusButton
          />

          {showKeyDetails && (
            <KeyDetails ref={keyDetails}>
              {!!property.numBedrooms && (
                <SummaryItem>
                  <BedsIcon color={theme.backgroundMid} className="pp-summary-icon pp-summary-icon-beds" />

                  <Text ml={10}>
                    {property.numBedrooms} {puralise('Bedroom', 'Bedrooms', property.numBedrooms)}
                  </Text>
                </SummaryItem>
              )}

              {!!property.numBathrooms && (
                <SummaryItem>
                  <BathsIcon color={theme.backgroundMid} className="pp-summary-icon pp-summary-icon-baths" />

                  <Text ml={10}>
                    {property.numBathrooms} {puralise('Bathroom', 'Bathrooms', property.numBathrooms)}
                  </Text>
                </SummaryItem>
              )}

              {!!property.numReceptionRooms && (
                <SummaryItem>
                  <ReceptionsIcon color={theme.backgroundMid} className="pp-summary-icon pp-summary-icon-receptions" />

                  <Text ml={10}>
                    {property.numReceptionRooms} {puralise('Reception', 'Receptions', property.numReceptionRooms)}
                  </Text>
                </SummaryItem>
              )}

              {(property.epc || property.ber) && (
                <SummaryItem>
                  <EnergyRatingIcon property={property} />
                </SummaryItem>
              )}
            </KeyDetails>
          )}

          <ShowHomesBox property={props.property} containerStyle={{ marginTop: 30 }} />

          {(property.type === 'development' || property.development) && (
            <AgentAwards awards={property.account.propertypalAwards} containerStyle={{ marginTop: 30 }} />
          )}
        </SummaryBox>

        <AgentBox className="pp-contact-agent" color={buttonColor}>
          {sellingAgent && (
            <>
              <AgentBoxHeader color={agentColor}>
                <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                  {sellingAgent.logo && sellingAgent.logo.premier && (
                    <AgentLogo data-testid="agent-logo" src={sellingAgent.logo.premier} alt="Agent Logo" />
                  )}
                </Link>
              </AgentBoxHeader>

              <Title>Contact {sellingAgent.organisation}</Title>
            </>
          )}

          {!sellingAgent && <Title>Marketed by multiple agents</Title>}

          {sellingAgent && sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
            <AgentText>
              PSR Licence: {sellingAgent.psrLicenceNumber}
              {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
                <LicenceWarning href="/psr-licence-number" prefetch={false}>
                  ?
                </LicenceWarning>
              )}
            </AgentText>
          )}

          <Text className="show-print" align="center" fontSize={18} mb={15}>
            {sellingAgent?.phoneNumber?.international}
          </Text>

          <AgentBoxContent className="hide-print">
            <AgentButtons>
              {!agentColor && sellingAgent && agentAward && property.type === 'property' && !property.development && (
                <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                  <AwardLogo src={agentAward.imageUrl} alt={agentAward.label} data-testid="award-logo" />
                </Link>
              )}

              {property.published && (
                <>
                  {props.registerInterest && (
                    <>
                      <Button
                        containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                        onClick={() => registerInterestClick('Enquiry Box')}
                        testID="registerInterestButton"
                        type="button"
                        className="pp-register-interest"
                        color={buttonColor}
                      >
                        Register Your Interest
                      </Button>

                      <HorizontalDivider
                        color={theme.textDark}
                        dividerColor={theme.backgroundLight}
                        text="or"
                        mb={15}
                      />
                    </>
                  )}

                  {contactInfo.email && (
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                      onClick={() => enquireNowClick('Enquiry Box')}
                      testID="enquireNowButton"
                      type="button"
                      color={buttonColor}
                    >
                      {props.registerInterest ? 'Email The Agent' : 'Enquire Now'}
                    </Button>
                  )}

                  {contactInfo.phone && (
                    <PhoneNumberButton
                      {...props}
                      color={buttonColor}
                      containerStyle={{ height: 50, margin: '0 auto 15px', maxWidth: 400 }}
                      location="Enquiry Box"
                    />
                  )}

                  {props.requestValuation && (
                    <>
                      <HorizontalDivider
                        color={theme.textDark}
                        dividerColor={theme.backgroundLight}
                        text="or"
                        mb={15}
                      />

                      <Button
                        containerStyle={{ height: 50, marginBottom: 15, maxWidth: 400 }}
                        onClick={requestValuationClick}
                        testID="requestValuationButton"
                        type="button"
                        color={buttonColor}
                      >
                        Request a Valuation
                      </Button>
                    </>
                  )}
                </>
              )}
            </AgentButtons>

            {agentColor && sellingAgent && (
              <AgentExtraInfo show={showMore}>
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faMapMarkerAlt} color={theme.textDark} style={{ fontSize: 18 }} />
                  </IconBox>

                  <Text>
                    {sellingAgent.displayAddress}
                    <br />
                    {sellingAgent.groupLandingPageUrl && (
                      <Link href={sellingAgent.groupLandingPageUrl}>
                        <LinkText as="span" fontSize={14} color={buttonColor}>
                          View our other branches <Chevron size={11} />
                        </LinkText>
                      </Link>
                    )}
                  </Text>
                </AgentListItem>

                {!!timeLabels.length && (
                  <AgentListItem>
                    <IconBox>
                      <FontAwesomeIcon icon={faClock} color={theme.textDark} style={{ fontSize: 18 }} />
                    </IconBox>

                    <div>
                      {timeLabels.map((timeLabel) => (
                        <Text key={timeLabel.firstDay}>
                          {timeLabel.firstDay}
                          {timeLabel.lastDay && ` to ${timeLabel.lastDay}`}: {timeLabel.openingTime}
                          {timeLabel.closingTime && `- ${timeLabel.closingTime}`}
                        </Text>
                      ))}
                    </div>
                  </AgentListItem>
                )}

                {sellingAgent.searchUrl && (
                  <AgentListItem>
                    <IconBox>
                      <FontAwesomeIcon icon={faHomeLgAlt} color={theme.textDark} style={{ fontSize: 18 }} />
                    </IconBox>

                    <Text>
                      <Link href={sellingAgent.searchUrl} prefetch={false}>
                        <LinkText as="span" fontSize={14} color={buttonColor}>
                          View our similar properties <Chevron size={11} />
                        </LinkText>
                      </Link>
                    </Text>
                  </AgentListItem>
                )}

                {agentAward && sellingAgent.landingPageUrl && (
                  <AgentListItem>
                    <IconBox>
                      <FontAwesomeIcon icon={faTrophy} color={theme.textDark} style={{ fontSize: 18 }} />
                    </IconBox>

                    <Link href={sellingAgent.landingPageUrl} prefetch={false}>
                      <AwardLogo
                        src={agentAward.imageUrl}
                        alt={agentAward.label}
                        style={{ marginTop: 0, maxHeight: 50 }}
                        data-testid="award-logo"
                      />
                    </Link>
                  </AgentListItem>
                )}
              </AgentExtraInfo>
            )}
          </AgentBoxContent>

          {!!agentColor && (
            <ShowMoreDetails
              color={buttonColor}
              onClick={() => setShowMore(true)}
              show={!showMore}
              className="hide-print"
            >
              Show more details <FontAwesomeIcon icon={faChevronDown} color={agentColor} />
            </ShowMoreDetails>
          )}
        </AgentBox>

        <AgentBoxFixed
          className="pp-contact-agent hide-print"
          style={{ top: showEnquiryBar ? 0 : -60, visibility: showEnquiryBar ? 'visible' : 'hidden' }}
        >
          {sellingAgent && (
            <RowAlignCenter>
              {sellingAgent.logo && sellingAgent.logo.premier && (
                <img
                  src={sellingAgent.logo.premier}
                  alt={`${property.development ? 'Developer' : 'Agent'} Logo`}
                  style={{ marginRight: 15 }}
                  data-testid="agent-logo-fixed"
                />
              )}
              <NameAndPSRTextWrapper>
                <AgentText>{sellingAgent.organisation}</AgentText>
                {sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
                  <AgentText>
                    PSR Licence: {sellingAgent.psrLicenceNumber}
                    {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
                      <LicenceWarning href="/psr-licence-number" prefetch={false} style={{ textAlign: 'center' }}>
                        ?
                      </LicenceWarning>
                    )}
                  </AgentText>
                )}
              </NameAndPSRTextWrapper>
            </RowAlignCenter>
          )}

          {!sellingAgent && (
            <RowAlignCenter>
              <AgentText>Marketed by multiple agents</AgentText>
            </RowAlignCenter>
          )}

          {property.published && (
            <AgentBoxFixedButtons numBtns={props.registerInterest ? 3 : 2}>
              {props.registerInterest && (
                <Button
                  containerStyle={{ height: 50, marginRight: 10 }}
                  onClick={() => registerInterestClick('Sticky Bar')}
                  testID="registerInterestFixed"
                  type="button"
                  className="pp-register-interest"
                >
                  Register Your Interest
                </Button>
              )}

              {contactInfo.email && (
                <Button
                  containerStyle={{ height: 50, marginRight: 10 }}
                  onClick={() => enquireNowClick('Sticky Bar')}
                  testID="enquireNowFixed"
                  type="button"
                >
                  {props.registerInterest ? 'Email The Agent' : 'Enquire Now'}
                </Button>
              )}

              {contactInfo.phone && (
                <PhoneNumberButton {...props} containerStyle={{ height: 50, marginRight: 10 }} location="Sticky Bar" />
              )}
            </AgentBoxFixedButtons>
          )}
        </AgentBoxFixed>
      </Container>

      <div ref={contactBox} />
    </>
  );
};

export default PropertySummary;
